import React, { useState, useEffect } from 'react'
import { Layout, Menu, Button, Dropdown, Avatar, Space } from 'antd'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  CalendarOutlined,
  ShopOutlined,
  UserOutlined,
  SettingOutlined,
  LockOutlined,
  LogoutOutlined,
  DownOutlined,
  BankOutlined,
  DollarOutlined,
  TeamOutlined,
  HistoryOutlined,
  FundProjectionScreenOutlined,
  FunnelPlotOutlined,
  BarChartOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import { useMediaQuery } from 'react-responsive'
import { useAuth } from '../../hooks/AuthContext'
import { message } from 'antd'

const { Header, Sider, Content } = Layout

// 莫兰迪色系配色
const colors = {
  background: 'linear-gradient(135deg, #f5f5f0 0%, #e8e8e8 50%, #f0ede5 100%)',
  cardBackground: '#faf9f6',
  sidebarBackground: 'linear-gradient(180deg, #a8b5c0 0%, #9ca3af 100%)',
  sidebarHover: 'rgba(255, 255, 255, 0.1)',
  textPrimary: '#5a5a5a',
  textSecondary: '#8a8a8a',
  border: 'rgba(168, 181, 192, 0.3)',
  shadow: 'rgba(154, 163, 175, 0.2)',
  buttonGradient: 'linear-gradient(135deg, #a8b5c0 0%, #9ca3af 100%)',
  buttonHover: 'linear-gradient(135deg, #9ca3af 0%, #8a939f 100%)'
}

const StyledLayout = styled(Layout)`
  min-height: 100vh;
  background: ${colors.background};
  width: 100%;
  overflow-x: hidden;
  box-sizing: border-box;
  @media (max-width: 768px) {
    .ant-layout-sider {
      position: fixed !important;
      height: 100vh;
      left: 0;
      top: 0;
      z-index: 999;
    }

    /* 确保布局不会超出屏幕 */
    max-width: 100vw;
    overflow-x: hidden;
  }
`

const StyledSider = styled(Sider)`
  background: ${colors.sidebarBackground} !important;
  box-shadow: 4px 0 16px ${colors.shadow};

  .ant-menu {
    background: transparent !important;
    border-right: none;
    padding: 16px 8px;
  }

  .ant-menu-item {
    border-radius: 12px;
    margin: 6px 0;
    height: 48px;
    line-height: 48px;
    transition: all 0.3s ease;

    &:hover {
      background: ${colors.sidebarHover} !important;
    }

    &.ant-menu-item-selected {
      background: rgba(255, 255, 255, 0.15) !important;
      color: #ffffff !important;
      font-weight: 500;

      &::after {
        display: none;
      }
    }

    a {
      color: rgba(255, 255, 255, 0.85);
      transition: color 0.3s ease;

      &:hover {
        color: #ffffff;
      }
    }

    &.ant-menu-item-selected a {
      color: #ffffff;
    }
  }

  .ant-menu-item-icon {
    font-size: 18px;
    color: rgba(255, 255, 255, 0.85);
  }

  .ant-menu-item-selected .ant-menu-item-icon {
    color: #ffffff;
  }

  @media (max-width: 768px) {
    box-shadow: 4px 0 24px rgba(0, 0, 0, 0.3);
  }
`

const LogoContainer = styled.div`
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  margin: 16px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  @media (max-width: 768px) {
    margin: 12px;
    padding: 10px;
  }
`

const LogoText = styled.div`
  color: #ffffff;
  font-size: 18px;
  font-weight: 500;
  letter-spacing: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`

const StyledHeader = styled(Header)`
  padding: 0 24px;
  background: ${colors.cardBackground};
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 16px ${colors.shadow};
  position: sticky;
  top: 0;
  z-index: 100;
  border-bottom: 1px solid ${colors.border};

  @media (max-width: 768px) {
    padding: 0 16px;
  }
`

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const MenuTrigger = styled.div`
  font-size: 20px;
  cursor: pointer;
  color: ${colors.textPrimary};
  padding: 8px;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(168, 181, 192, 0.1);
    color: ${colors.textPrimary};
  }
`

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    gap: 8px;
  }
`

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 12px;
  transition: all 0.3s ease;
  border: 1px solid ${colors.border};

  &:hover {
    background: rgba(168, 181, 192, 0.1);
  }

  @media (max-width: 768px) {
    padding: 4px 8px;
  }
`

const Username = styled.span`
  color: ${colors.textPrimary};
  font-weight: 500;
  font-size: 14px;

  @media (max-width: 768px) {
    display: none;
  }
`

const StyledButton = styled(Button)`
  background: ${colors.buttonGradient};
  border: none;
  color: #ffffff;
  border-radius: 12px;
  height: 36px;
  font-weight: 500;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px ${colors.shadow};

  &:hover {
    background: ${colors.buttonHover};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${colors.shadow};
    color: #ffffff;
  }

  &:active {
    transform: translateY(0);
  }

  &.ant-btn-dangerous {
    background: linear-gradient(135deg, #c89b9b 0%, #b88a8a 100%);

    &:hover {
      background: linear-gradient(135deg, #b88a8a 0%, #a87979 100%);
    }
  }

  @media (max-width: 768px) {
    height: 32px;
    font-size: 12px;
    padding: 0 12px;
  }
`

const StyledContent = styled(Content)`
  margin: 24px;
  padding: 0;
  min-height: calc(100vh - 112px);
  background: transparent;
  overflow-x: hidden;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    margin: 12px 8px;
    padding: 0;
    min-height: calc(100vh - 96px);
    max-width: 100vw;
    overflow-x: hidden;
  }
`

const ContentCard = styled.div`
  background: ${colors.cardBackground};
  border-radius: 16px;
  box-shadow: 0 4px 16px ${colors.shadow};
  border: 1px solid ${colors.border};
  padding: 24px;
  min-height: 280px;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: 12px;
    border-radius: 12px;
    max-width: 100%;
    overflow-x: hidden;
  }

  /* 确保所有子元素不会超出 */
  * {
    max-width: 100%;
    box-sizing: border-box;
  }
`

const MobileOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 998;
  backdrop-filter: blur(2px);
`

const AdminLayout = ({ children }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const isMobile = useMediaQuery({ maxWidth: 768 })
  const [collapsed, setCollapsed] = useState(isMobile)
  const [menuOpenKeys, setMenuOpenKeys] = useState([])

  useEffect(() => {
    // 移动端默认收起侧边栏
    if (isMobile) {
      setCollapsed(true)
    }
  }, [isMobile])

  useEffect(() => {
    // 当路径改变时，如果是移动端则收起侧边栏
    if (isMobile) {
      setCollapsed(true)
    }
  }, [location.pathname, isMobile])

  useEffect(() => {
    // 進到子頁時自動展開所屬的父選單；離開時把該父選單收起來。
    // 只動「由路徑推導出來的」那幾個 key，使用者手動展開的其他選單不受影響。
    const AUTO_KEYS = ['store-mgmt', 'reports']
    const underStore =
      location.pathname.startsWith('/admin/stores') ||
      location.pathname === '/admin/business-users'
    const underReports = location.pathname.startsWith('/admin/reports')

    let activeKey = null
    if (underStore) activeKey = 'store-mgmt'
    else if (underReports) activeKey = 'reports'

    setMenuOpenKeys((prev) => {
      const kept = prev.filter((k) => !AUTO_KEYS.includes(k))
      return activeKey ? [...kept, activeKey] : kept
    })
  }, [location.pathname])

  const handleMenuClick = () => {
    // 移动端点击菜单后自动收起
    if (isMobile) {
      setCollapsed(true)
    }
  }

  const handleLogout = async () => {
    try {
      await logout()
      message.success('已成功登出')
      navigate('/admin/login')
    } catch (error) {
      console.error('登出失敗:', error)
      message.error('登出失敗，請稍後再試')
    }
  }

  // 右上角固定顯示平台品牌名（admin 後台的使用者一定是雲耘的人），
  // 實際登入的帳號放到下拉選單裡，需要確認身分時還是看得到。
  const accountLabel = user?.username || user?.email || '管理員'

  const userMenuItems = [
    {
      key: 'account',
      label: (
        <div style={{ lineHeight: 1.5 }}>
          <div style={{ fontSize: 12, color: colors.textSecondary }}>
            登入帳號
          </div>
          <div style={{ fontWeight: 500 }}>{accountLabel}</div>
        </div>
      ),
      disabled: true
    },
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '登出',
      danger: true
    }
  ]

  const handleUserMenuClick = ({ key }) => {
    if (key === 'logout') {
      handleLogout()
    }
  }

  const menuItems = [
    {
      key: '/admin/dashboard',
      icon: <DashboardOutlined />,
      label: (
        <Link to='/admin/dashboard' onClick={handleMenuClick}>
          儀表板
        </Link>
      )
    },
    {
      key: 'reports',
      icon: <BarChartOutlined />,
      label: '數據報表',
      children: [
        {
          key: '/admin/reports/kpi',
          label: (
            <Link to='/admin/reports/kpi' onClick={handleMenuClick}>
              KPI 統計
            </Link>
          )
        }
      ]
    },
    {
      key: 'store-mgmt',
      icon: <ShopOutlined />,
      label: '店家管理',
      children: [
        {
          key: '/admin/stores',
          label: (
            <Link to='/admin/stores' onClick={handleMenuClick}>
              店家申請管理
            </Link>
          )
        },
        {
          key: '/admin/business-users',
          label: (
            <Link to='/admin/business-users' onClick={handleMenuClick}>
              店家用戶管理
            </Link>
          )
        },
        {
          key: '/admin/stores/active',
          label: (
            <Link to='/admin/stores/active' onClick={handleMenuClick}>
              使用中店家
            </Link>
          )
        }
      ]
    },
    {
      key: '/admin/kpi-report',
      icon: <FundProjectionScreenOutlined />,
      label: (
        <Link to='/admin/kpi-report' onClick={handleMenuClick}>
          KPI 成效報表
        </Link>
      )
    },
    {
      key: '/admin/funnel-report',
      icon: <FunnelPlotOutlined />,
      label: (
        <Link to='/admin/funnel-report' onClick={handleMenuClick}>
          前台轉換與來源
        </Link>
      )
    },
    {
      key: '/admin/payment-applications',
      icon: <BankOutlined />,
      label: (
        <Link to='/admin/payment-applications' onClick={handleMenuClick}>
          支付申請管理
        </Link>
      )
    },
    {
      key: '/admin/credit-payments',
      icon: <DollarOutlined />,
      label: (
        <Link to='/admin/credit-payments' onClick={handleMenuClick}>
          交易記錄管理
        </Link>
      )
    },
    {
      key: '/admin/bookings',
      icon: <CalendarOutlined />,
      label: (
        <Link to='/admin/bookings' onClick={handleMenuClick}>
          預約管理
        </Link>
      )
    },
    {
      key: '/admin/users',
      icon: <UserOutlined />,
      label: (
        <Link to='/admin/users' onClick={handleMenuClick}>
          用戶管理
        </Link>
      )
    },
    {
      key: '/admin/platform-admins',
      icon: <TeamOutlined />,
      label: (
        <Link to='/admin/platform-admins' onClick={handleMenuClick}>
          後台管理員帳號
        </Link>
      )
    },
    {
      key: '/admin/article-permission',
      icon: <SettingOutlined />,
      label: (
        <Link to='/admin/article-permission' onClick={handleMenuClick}>
          文章權限管理
        </Link>
      )
    },
    {
      key: '/admin/settings',
      icon: <SettingOutlined />,
      label: (
        <Link to='/admin/settings' onClick={handleMenuClick}>
          系統設置
        </Link>
      )
    },
    {
      key: '/admin/change-password',
      icon: <LockOutlined />,
      label: (
        <Link to='/admin/change-password' onClick={handleMenuClick}>
          修改店家密碼
        </Link>
      )
    },
    {
      key: '/admin/changelog',
      icon: <HistoryOutlined />,
      label: (
        <Link to='/admin/changelog' onClick={handleMenuClick}>
          系統更新紀錄
        </Link>
      )
    }
  ]

  return (
    <StyledLayout>
      <StyledSider
        trigger={null}
        collapsible
        collapsed={collapsed}
        breakpoint='lg'
        collapsedWidth={isMobile ? 0 : 80}
        onBreakpoint={(broken) => {
          if (!isMobile) {
            setCollapsed(broken)
          }
        }}
        style={{
          overflow: 'auto',
          position: isMobile ? 'fixed' : 'relative',
          left: 0,
          top: 0
        }}
      >
        <LogoContainer>
          {!collapsed && <LogoText>管理後台</LogoText>}
          {collapsed && !isMobile && <LogoText>管理</LogoText>}
        </LogoContainer>
        <Menu
          theme='dark'
          mode='inline'
          selectedKeys={[location.pathname]}
          openKeys={menuOpenKeys}
          onOpenChange={setMenuOpenKeys}
          items={menuItems}
        />
      </StyledSider>
      {isMobile && !collapsed && (
        <MobileOverlay onClick={() => setCollapsed(true)} />
      )}
      <Layout
        style={{
          transition: 'all 0.2s',
          width: isMobile ? '100%' : `calc(100% - ${collapsed ? 80 : 200}px)`,
          maxWidth: '100%',
          overflowX: 'hidden',
          boxSizing: 'border-box'
        }}
      >
        <StyledHeader>
          <HeaderLeft>
            <MenuTrigger onClick={() => setCollapsed(!collapsed)}>
              {React.createElement(
                collapsed ? MenuUnfoldOutlined : MenuFoldOutlined
              )}
            </MenuTrigger>
          </HeaderLeft>
          <HeaderRight>
            <Dropdown
              menu={{
                items: userMenuItems,
                onClick: handleUserMenuClick
              }}
              placement='bottomRight'
            >
              <UserInfo>
                <Avatar
                  size='small'
                  icon={<UserOutlined />}
                  style={{
                    background: colors.buttonGradient,
                    flexShrink: 0
                  }}
                />
                <Username>雲耘科技有限公司</Username>
                <DownOutlined
                  style={{ fontSize: '12px', color: colors.textSecondary }}
                />
              </UserInfo>
            </Dropdown>
            {!isMobile && (
              <StyledButton icon={<LogoutOutlined />} onClick={handleLogout}>
                登出
              </StyledButton>
            )}
          </HeaderRight>
        </StyledHeader>
        <StyledContent>
          <ContentCard>{children}</ContentCard>
        </StyledContent>
      </Layout>
    </StyledLayout>
  )
}

export default AdminLayout
